import React, { useState, useRef } from 'react';
import { PipelineProject, PipelineStage, getStage, getNextStage } from '../../types/pipeline';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from './StatusBadge';
import ProgressBar from './ProgressBar';
import AdvanceStageModal from './AdvanceStageModal';

interface StageBoardProps {
  projects: PipelineProject[];
  stages: PipelineStage[];
  onSelectProject: (project: PipelineProject) => void;
  onProjectUpdated?: () => void;
}

const StageBoard: React.FC<StageBoardProps> = ({ projects, stages, onSelectProject, onProjectUpdated }) => {
  const { user } = useAuth();
  const userType = user?.user_type || 'member';
  const canDrag = userType === 'admin' || userType === 'super_admin' || userType === 'member';

  const [draggedProject, setDraggedProject] = useState<PipelineProject | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [advanceProject, setAdvanceProject] = useState<PipelineProject | null>(null);
  const dragCounter = useRef<Record<string, number>>({});

  const grouped = stages.map((stage) => ({
    stage,
    projects: projects.filter((p) => p.stage === stage.id),
  }));

  const handleDragStart = (e: React.DragEvent, project: PipelineProject) => {
    if (!canDrag || project.status !== 'active') {
      e.preventDefault();
      return;
    }
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
    // Use a tiny transparent image as drag ghost
    const ghost = document.createElement('div');
    ghost.style.opacity = '0';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleDragEnd = () => {
    setDraggedProject(null);
    setDropTarget(null);
    dragCounter.current = {};
  };

  const handleDragEnter = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    dragCounter.current[stageId] = (dragCounter.current[stageId] || 0) + 1;
    if (draggedProject) {
      const nextStage = getNextStage(draggedProject.stage);
      if (nextStage && nextStage.id === stageId) {
        setDropTarget(stageId);
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    dragCounter.current[stageId] = (dragCounter.current[stageId] || 0) - 1;
    if (dragCounter.current[stageId] <= 0) {
      dragCounter.current[stageId] = 0;
      if (dropTarget === stageId) {
        setDropTarget(null);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    setDropTarget(null);
    dragCounter.current = {};

    if (!draggedProject) return;

    const nextStage = getNextStage(draggedProject.stage);
    if (nextStage && nextStage.id === stageId) {
      setAdvanceProject(draggedProject);
    }
    setDraggedProject(null);
  };

  return (
    <>
      <div className="flex gap-3 overflow-x-auto pb-4 min-h-[400px]">
        {grouped.map(({ stage, projects: stageProjects }) => {
          const isValidDrop = draggedProject && getNextStage(draggedProject.stage)?.id === stage.id;
          const isHighlighted = dropTarget === stage.id;

          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-56 flex flex-col"
              onDragEnter={(e) => handleDragEnter(e, stage.id)}
              onDragLeave={(e) => handleDragLeave(e, stage.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.id)}
            >
              {/* Column header */}
              <div
                className="px-3 py-2 rounded-t-lg flex items-center justify-between"
                style={{ backgroundColor: `${stage.color}12`, borderBottom: `2px solid ${stage.color}` }}
              >
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: stage.color }}>
                  {stage.short}
                </span>
                <span className="text-xs text-gray-400 font-medium">{stageProjects.length}</span>
              </div>

              {/* Cards area */}
              <div
                className={`flex-1 rounded-b-lg p-2 space-y-2 transition-colors duration-200 ${
                  isHighlighted
                    ? 'bg-teal-50 ring-2 ring-teal-300 ring-inset'
                    : isValidDrop && draggedProject
                    ? 'bg-gray-50/80'
                    : 'bg-gray-50/50'
                }`}
              >
                {stageProjects.length === 0 && !isHighlighted && (
                  <div className="text-xs text-gray-300 text-center py-6">No programs</div>
                )}
                {isHighlighted && stageProjects.length === 0 && (
                  <div className="text-xs text-teal-500 text-center py-6 font-medium">
                    Drop to advance
                  </div>
                )}
                {stageProjects.map((p, cardIdx) => {
                  const isDragging = draggedProject?.id === p.id;
                  return (
                    <div
                      key={p.id}
                      draggable={canDrag && p.status === 'active'}
                      onDragStart={(e) => handleDragStart(e, p)}
                      onDragEnd={handleDragEnd}
                      className={`bg-white rounded-lg border border-gray-200 p-3 transition-all duration-200 ${
                        canDrag && p.status === 'active' ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                      } ${isDragging ? 'opacity-40 scale-95' : 'hover:shadow-md hover:border-gray-300'}`}
                      onClick={() => !draggedProject && onSelectProject(p)}
                      style={{
                        opacity: isDragging ? 0.4 : undefined,
                        animation: isDragging ? 'none' : `fadeSlideUp 0.3s ease forwards`,
                        animationDelay: isDragging ? '0ms' : `${cardIdx * 80}ms`,
                      }}
                    >
                      <div className="flex items-start justify-between mb-1.5">
                        <h4 className="text-sm font-semibold text-gray-900">{p.name}</h4>
                        <StatusBadge status={p.status} />
                      </div>
                      <p className="text-xs text-gray-400 mb-2">{p.target}</p>

                      <ProgressBar value={p.progress} height="h-1.5" className="mb-2" />

                      <div className="text-xs text-gray-500">
                        <span>{p.compounds.toLocaleString()} compounds</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* CSS for card animation */}
        <style>{`
          @keyframes fadeSlideUp {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>

      {/* Advance Modal triggered by drag-and-drop */}
      {advanceProject && (() => {
        const cs = getStage(advanceProject.stage);
        const ns = getNextStage(advanceProject.stage);
        if (!cs || !ns) return null;
        return (
          <AdvanceStageModal
            projectId={advanceProject.id}
            projectName={advanceProject.name}
            currentStage={cs}
            nextStage={ns}
            onClose={() => setAdvanceProject(null)}
            onAdvanced={() => {
              setAdvanceProject(null);
              onProjectUpdated?.();
            }}
          />
        );
      })()}
    </>
  );
};

export default StageBoard;
